import { useCallback, useEffect, useRef, useState } from 'react';
import { Gavel, Loader2 } from 'lucide-react';
import { pressureTestAssess, pressureTestQuestion } from '@/lib/api-client';
import { isPreHearingAcked } from '@/lib/prehearing';
import type { Turn } from '@/lib/types';
import { useSession } from '@/contexts/SessionContext';
import { useTranscriptionContext } from '@/contexts/TranscriptionContext';

// Rehearsal pressure test (Operation Friday Ready P1-5). One button: Next
// question. The server plays a firm-but-fair judge grounded in the matter
// documents. The operator answers ALOUD — the live transcript captures it —
// and after a short silence the answer is sent for a compact strong/tighten
// assessment. Deliberately simple: no queues, no history UI (rows persist to
// the session feed via the Response table), one question on screen at a time.

// The operator has finished answering when no new final turn lands for this long.
const ANSWER_SILENCE_MS = 6_000;
// Don't assess a cough or a two-word false start.
const MIN_ANSWER_CHARS = 40;

function answerText(turns: Turn[], fromIndex: number): string {
  return turns
    .slice(fromIndex)
    .map((t) => t.text)
    .join(' ')
    .trim();
}

export function PressureTestPanel() {
  const { sessionId, rehearsal } = useSession();
  const { turns, isListening } = useTranscriptionContext();

  const [active, setActive] = useState(false);
  const [question, setQuestion] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<string | null>(null);
  const [busy, setBusy] = useState<'question' | 'assess' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Index into `turns` where the current answer starts (set when the question lands).
  const answerFromRef = useRef(0);
  const assessedRef = useRef(true); // true = nothing pending assessment
  const silenceTimerRef = useRef<number | null>(null);
  const turnsRef = useRef(turns);
  turnsRef.current = turns;

  const clearSilenceTimer = (): void => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const runAssess = useCallback(
    async (q: string, answer: string): Promise<void> => {
      if (!sessionId) {
        return;
      }
      assessedRef.current = true;
      setBusy('assess');
      setError(null);
      try {
        const env = await pressureTestAssess(sessionId, q, answer.slice(0, 8_000));
        setAssessment(env.response.answer);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Assessment failed');
      } finally {
        setBusy(null);
      }
    },
    [sessionId],
  );

  const nextQuestion = useCallback(async (): Promise<void> => {
    if (!sessionId || busy) {
      return;
    }
    // Same lawful-use gate as listening: no generation before the pre-hearing
    // acknowledgement has been given for this session.
    if (!isPreHearingAcked(sessionId)) {
      setError('Acknowledge the pre-hearing notice (start listening) first.');
      return;
    }
    clearSilenceTimer();
    // An unassessed spoken answer gets assessed before the next question.
    if (question && !assessedRef.current) {
      const answer = answerText(turnsRef.current, answerFromRef.current);
      if (answer.length >= MIN_ANSWER_CHARS) {
        await runAssess(question, answer);
      } else {
        assessedRef.current = true;
      }
    }
    setBusy('question');
    setError(null);
    try {
      const env = await pressureTestQuestion(sessionId);
      setQuestion(env.response.answer);
      setAssessment(null);
      answerFromRef.current = turnsRef.current.length;
      assessedRef.current = false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate a question');
    } finally {
      setBusy(null);
    }
  }, [sessionId, busy, question, runAssess]);

  // Auto-assess: once the operator has spoken and then paused, send the answer.
  useEffect(() => {
    if (!active || !question || assessedRef.current || busy) {
      return;
    }
    if (turns.length <= answerFromRef.current) {
      return; // nothing spoken yet
    }
    clearSilenceTimer();
    silenceTimerRef.current = window.setTimeout(() => {
      silenceTimerRef.current = null;
      const answer = answerText(turnsRef.current, answerFromRef.current);
      if (answer.length >= MIN_ANSWER_CHARS && question) {
        void runAssess(question, answer);
      }
    }, ANSWER_SILENCE_MS);
    return clearSilenceTimer;
  }, [turns, active, question, busy, runAssess]);

  if (!rehearsal || !sessionId) {
    return null;
  }

  return (
    <div className="rounded-md border border-stone-800/70 bg-stone-900/40 px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-stone-500">
          <Gavel className="h-3 w-3 text-amber-500" aria-hidden />
          Pressure test
          {busy ? <Loader2 className="h-3 w-3 animate-spin text-stone-500" aria-hidden /> : null}
        </span>
        <button
          type="button"
          onClick={() => setActive((v) => !v)}
          className={
            active
              ? 'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-[0.2em] text-amber-400 hover:text-amber-300'
              : 'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-[0.2em] text-stone-600 hover:text-stone-400'
          }
        >
          {active ? 'On' : 'Off'}
        </button>
      </div>

      {!active ? (
        <p className="mt-2 text-[11px] text-stone-600">
          Rehearse against a firm-but-fair judge. Questions come from your case documents; answer
          out loud and a short assessment follows.
        </p>
      ) : (
        <div className="mt-2 space-y-2">
          {question ? (
            <div className="rounded border border-amber-900/40 bg-amber-950/20 px-2.5 py-2">
              <p className="text-[9px] uppercase tracking-[0.2em] text-amber-600">The bench asks</p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-snug text-amber-100">
                {question}
              </p>
            </div>
          ) : null}
          {question && !assessment && !busy ? (
            <p className="text-[10px] text-stone-500">
              {isListening
                ? assessedRef.current
                  ? null
                  : 'Answer out loud — an assessment appears when you pause.'
                : 'Start listening so your spoken answer is captured.'}
            </p>
          ) : null}
          {busy === 'assess' ? (
            <p className="text-[10px] text-stone-500">Assessing your answer…</p>
          ) : null}
          {assessment ? (
            <div className="rounded border border-stone-800 bg-stone-950/60 px-2.5 py-2">
              <p className="text-[9px] uppercase tracking-[0.2em] text-stone-500">Assessment</p>
              <p className="mt-1 whitespace-pre-wrap text-xs leading-snug text-stone-300">
                {assessment}
              </p>
            </div>
          ) : null}
          {error ? <p className="text-xs text-red-400">{error}</p> : null}
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void nextQuestion()}
            className="w-full rounded-md border border-amber-700/50 bg-amber-950/20 px-3 py-2 text-xs font-medium text-amber-300 transition-colors hover:bg-amber-950/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy === 'question' ? 'The bench is thinking…' : question ? 'Next question' : 'First question'}
          </button>
        </div>
      )}
    </div>
  );
}
